import { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Clock, GripVertical, MoreVertical, ArrowLeftRight, Eraser, Plus } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";

/**
 * Spotify-style timeline editor.
 * Lanes = canchas (courts) horizontal rows.
 * Slots = time blocks (computed from start/end + duration).
 * Each match is a draggable pill placed at its (cancha, hora).
 *
 * Props:
 *   matches: [{ id, hora, cancha, local, visitante, branch, status }]
 *   numCourts
 *   startTime "HH:MM"
 *   endTime   "HH:MM"
 *   duration  minutes
 *   onMove(matchId, { hora, cancha })
 *   onSwap(matchId, otherMatchId) — intercambia slot entre dos partidos
 *   draftMatches: partidos en la bolsa (status=draft) del mismo torneo
 */
export default function TimelineEditor({
    matches,
    numCourts,
    startTime,
    endTime,
    duration,
    onMove,
    onSwap,
    onRelease,
    onFillSlot,
    draftMatches = [],
}) {
    const [swapDialog, setSwapDialog] = useState(null); // match seleccionado para swap con bolsa
    const [fillDialog, setFillDialog] = useState(null); // slot vacío { hora, cancha }
    const [swapFilter, setSwapFilter] = useState("");
    const [swapMode, setSwapMode] = useState("simple"); // 'simple' | 'vs'
    const [swapTeamA, setSwapTeamA] = useState("");
    const [swapTeamB, setSwapTeamB] = useState("");
    const [tramoFilter, setTramoFilter] = useState("all"); // all | ida | vuelta

    const slots = computeSlots(startTime, endTime, duration);
    // Build grid: lane -> slot -> match
    const grid = {};
    for (let c = 1; c <= numCourts; c++) {
        grid[c] = {};
        slots.forEach((s) => (grid[c][s] = null));
    }
    matches.forEach((m) => {
        if (!m.hora) return;
        const lane = m.cancha;
        if (grid[lane] && grid[lane][m.hora] === null) {
            grid[lane][m.hora] = m;
        } else if (grid[lane]) {
            grid[lane][m.hora] = m;
        }
    });

    const onDragEnd = (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        )
            return;
        const [destCourt, destSlot] = destination.droppableId.split("|");
        const target = grid[parseInt(destCourt)]?.[destSlot];
        if (target && target.id !== draggableId && onSwap) {
            // Slot ocupado -> intercambiar partidos
            onSwap(draggableId, target.id);
            return;
        }
        onMove(draggableId, { cancha: parseInt(destCourt), hora: destSlot });
    };

    const openSwapDialog = (match) => {
        setSwapFilter("");
        setTramoFilter("all");
        setSwapDialog(match);
    };

    const openFillDialog = (hora, cancha) => {
        setSwapFilter("");
        setTramoFilter("all");
        setFillDialog({ hora, cancha });
    };

    const handleBagSwap = (draftId) => {
        if (!swapDialog || !onSwap) return;
        onSwap(swapDialog.id, draftId);
        setSwapDialog(null);
    };

    const handleFillSlot = (draftId) => {
        if (!fillDialog || !onFillSlot) return;
        onFillSlot(draftId, {
            hora: fillDialog.hora,
            cancha: fillDialog.cancha,
        });
        setFillDialog(null);
    };

    const norm = (s) =>
        (s || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .trim();

    const filteredDrafts = draftMatches.filter((d) => {
        // Filtro por ida/vuelta
        if (tramoFilter === "ida" && d.vuelta === true) return false;
        if (tramoFilter === "vuelta" && d.vuelta !== true) return false;
        if (swapMode === "vs") {
            const a = norm(swapTeamA);
            const b = norm(swapTeamB);
            if (!a && !b) return true;
            const nl = norm(d.local);
            const nv = norm(d.visitante);
            // Ambos equipos deben aparecer (en cualquier orden)
            if (a && b) {
                return (
                    (nl.includes(a) && nv.includes(b)) ||
                    (nl.includes(b) && nv.includes(a))
                );
            }
            const only = a || b;
            return nl.includes(only) || nv.includes(only);
        }
        // simple
        if (!swapFilter.trim()) return true;
        const raw = norm(swapFilter);
        const tokens = raw
            .split(/\s+vs\s+|\s+-\s+|\s+/)
            .map((t) => t.trim())
            .filter(Boolean);
        const hay = norm(
            [d.local, d.visitante, d.branch, d.category_name]
                .filter(Boolean)
                .join(" "),
        );
        return tokens.every((tok) => hay.includes(tok));
    });

    return (
        <div className="vp-card p-0 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center gap-2 text-slate-700">
                    <Clock className="w-4 h-4" />
                    <span className="font-display font-bold">
                        Línea de tiempo
                    </span>
                </div>
                <span className="text-xs text-slate-500">
                    Arrastra para intercambiar · Usa el menú · para cambiar por
                    uno de la bolsa
                </span>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
                <div className="overflow-x-auto">
                    <div
                        className="grid"
                        style={{
                            gridTemplateColumns: `120px repeat(${slots.length}, minmax(160px, 1fr))`,
                        }}
                    >
                        {/* header row */}
                        <div className="bg-white sticky left-0 z-10 px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100">
                            Cancha
                        </div>
                        {slots.map((s) => (
                            <div
                                key={s}
                                className="px-3 py-3 text-xs font-bold text-slate-500 border-b border-slate-100 text-center"
                            >
                                {s}
                            </div>
                        ))}

                        {/* rows */}
                        {Array.from({ length: numCourts }).map((_, idx) => {
                            const c = idx + 1;
                            return (
                                <div key={c} className="contents">
                                    <div className="bg-slate-50 sticky left-0 z-10 px-4 py-6 text-sm font-bold border-b border-slate-100 flex items-center">
                                        Cancha {c}
                                    </div>
                                    {slots.map((s) => {
                                        const droppableId = `${c}|${s}`;
                                        const match = grid[c]?.[s];
                                        return (
                                            <Droppable
                                                key={droppableId}
                                                droppableId={droppableId}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.droppableProps}
                                                        className={`court-stripe min-h-[80px] border-b border-r border-slate-100 p-2 transition-colors ${
                                                            snapshot.isDraggingOver
                                                                ? "bg-ipn-50"
                                                                : ""
                                                        }`}
                                                    >
                                                        {match && (
                                                            <Draggable
                                                                draggableId={
                                                                    match.id
                                                                }
                                                                index={0}
                                                            >
                                                                {(p, snap) => (
                                                                    <div
                                                                        ref={
                                                                            p.innerRef
                                                                        }
                                                                        {...p.draggableProps}
                                                                        data-testid={`timeline-pill-${match.id}`}
                                                                        className={`pill-match relative ${
                                                                            match.branch ===
                                                                            "femenil"
                                                                                ? "bg-rose-50 border-rose-200 text-rose-900"
                                                                                : "bg-ipn-50 border-ipn-200 text-ipn-950"
                                                                        } ${
                                                                            snap.isDragging
                                                                                ? "rotate-2 shadow-lg"
                                                                                : ""
                                                                        } ${
                                                                            match.status ===
                                                                            "played"
                                                                                ? "opacity-60"
                                                                                : ""
                                                                        }`}
                                                                    >
                                                                        <div className="flex items-start justify-between gap-1">
                                                                            <div
                                                                                {...p.dragHandleProps}
                                                                                className="flex items-center gap-1 text-[10px] uppercase font-bold opacity-60 cursor-grab flex-1 min-w-0"
                                                                            >
                                                                                <GripVertical className="w-3 h-3 shrink-0" />
                                                                                <span className="truncate">
                                                                                    {
                                                                                        match.branch
                                                                                    }
                                                                                </span>
                                                                                {match.vuelta !==
                                                                                    undefined && (
                                                                                    <span
                                                                                        className={`ml-1 px-1 rounded-sm text-[9px] font-black ${
                                                                                            match.vuelta
                                                                                                ? "bg-amber-100 text-amber-800"
                                                                                                : "bg-sky-100 text-sky-800"
                                                                                        }`}
                                                                                    >
                                                                                        {match.vuelta
                                                                                            ? "V"
                                                                                            : "I"}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            {match.status !==
                                                                                "played" && (
                                                                                <DropdownMenu>
                                                                                    <DropdownMenuTrigger
                                                                                        asChild
                                                                                        onPointerDown={(
                                                                                            e,
                                                                                        ) =>
                                                                                            e.stopPropagation()
                                                                                        }
                                                                                    >
                                                                                        <button
                                                                                            data-testid={`timeline-kebab-${match.id}`}
                                                                                            className="p-0.5 rounded hover:bg-black/10 transition-colors"
                                                                                            aria-label="Más opciones"
                                                                                        >
                                                                                            <MoreVertical className="w-4 h-4" />
                                                                                        </button>
                                                                                    </DropdownMenuTrigger>
                                                                                    <DropdownMenuContent
                                                                                        align="end"
                                                                                        className="w-64"
                                                                                    >
                                                                                        <DropdownMenuItem
                                                                                            data-testid={`timeline-swap-bag-${match.id}`}
                                                                                            onClick={() =>
                                                                                                openSwapDialog(
                                                                                                    match,
                                                                                                )
                                                                                            }
                                                                                            disabled={
                                                                                                draftMatches.length ===
                                                                                                0
                                                                                            }
                                                                                        >
                                                                                            <ArrowLeftRight className="w-4 h-4 mr-2" />
                                                                                            Cambiar por un partido
                                                                                            de la bolsa
                                                                                        </DropdownMenuItem>
                                                                                        <DropdownMenuItem
                                                                                            data-testid={`timeline-release-${match.id}`}
                                                                                            onClick={() =>
                                                                                                onRelease &&
                                                                                                onRelease(
                                                                                                    match,
                                                                                                )
                                                                                            }
                                                                                            disabled={
                                                                                                !onRelease
                                                                                            }
                                                                                        >
                                                                                            <Eraser className="w-4 h-4 mr-2" />
                                                                                            Dejar espacio vacío
                                                                                        </DropdownMenuItem>
                                                                                    </DropdownMenuContent>
                                                                                </DropdownMenu>
                                                                            )}
                                                                        </div>
                                                                        <div
                                                                            {...p.dragHandleProps}
                                                                            className="leading-tight mt-1 cursor-grab"
                                                                        >
                                                                            <div className="truncate">
                                                                                {
                                                                                    match.local
                                                                                }
                                                                            </div>
                                                                            <div className="text-[11px] opacity-70">
                                                                                vs
                                                                            </div>
                                                                            <div className="truncate">
                                                                                {
                                                                                    match.visitante
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        )}
                                                        {!match && onFillSlot && (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    openFillDialog(
                                                                        s,
                                                                        c,
                                                                    )
                                                                }
                                                                disabled={
                                                                    draftMatches.length ===
                                                                    0
                                                                }
                                                                className="w-full h-full min-h-[72px] rounded-xl border-2 border-dashed border-slate-200 hover:border-ipn-500 hover:bg-ipn-50 text-slate-400 hover:text-ipn-700 flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                                data-testid={`timeline-add-${c}-${s}`}
                                                                title={
                                                                    draftMatches.length ===
                                                                    0
                                                                        ? "No hay partidos en la bolsa"
                                                                        : `Añadir partido a Cancha ${c} · ${s}`
                                                                }
                                                            >
                                                                <Plus className="w-5 h-5" />
                                                                <span className="text-[10px] uppercase font-bold tracking-widest">
                                                                    Añadir
                                                                </span>
                                                            </button>
                                                        )}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </DragDropContext>

            <Dialog
                open={!!swapDialog || !!fillDialog}
                onOpenChange={(o) => {
                    if (!o) {
                        setSwapDialog(null);
                        setFillDialog(null);
                    }
                }}
            >
                <DialogContent
                    className="max-w-lg"
                    data-testid="swap-bag-dialog"
                >
                    <DialogHeader>
                        <DialogTitle>
                            {fillDialog
                                ? "Añadir partido desde la bolsa"
                                : "Cambiar por un partido de la bolsa"}
                        </DialogTitle>
                        <DialogDescription>
                            {fillDialog
                                ? `El partido seleccionado se programará en ${fillDialog.hora} · Cancha ${fillDialog.cancha}.`
                                : `El partido programado se enviará a la bolsa y el seleccionado tomará su lugar (${swapDialog?.hora} · Cancha ${swapDialog?.cancha}).`}
                        </DialogDescription>
                    </DialogHeader>
                    <div
                        className="inline-flex bg-slate-100 rounded-xl p-1 mb-2"
                        data-testid="swap-bag-mode-toggle"
                    >
                        <button
                            type="button"
                            onClick={() => setSwapMode("simple")}
                            className={`px-3 h-8 rounded-lg text-xs font-bold transition-colors ${
                                swapMode === "simple"
                                    ? "bg-white text-ipn-900 shadow-sm"
                                    : "text-slate-500 hover:text-ipn-800"
                            }`}
                            data-testid="swap-bag-mode-simple"
                        >
                            Búsqueda simple
                        </button>
                        <button
                            type="button"
                            onClick={() => setSwapMode("vs")}
                            className={`px-3 h-8 rounded-lg text-xs font-bold transition-colors ${
                                swapMode === "vs"
                                    ? "bg-white text-ipn-900 shadow-sm"
                                    : "text-slate-500 hover:text-ipn-800"
                            }`}
                            data-testid="swap-bag-mode-vs"
                        >
                            Equipo vs Equipo
                        </button>
                    </div>
                    {swapMode === "simple" ? (
                        <Input
                            placeholder="Buscar equipo, rama, 'Equipo A vs Equipo B'…"
                            value={swapFilter}
                            onChange={(e) => setSwapFilter(e.target.value)}
                            className="h-10 rounded-xl"
                            data-testid="swap-bag-search"
                        />
                    ) : (
                        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                            <Input
                                placeholder="Equipo A"
                                value={swapTeamA}
                                onChange={(e) => setSwapTeamA(e.target.value)}
                                className="h-10 rounded-xl"
                                data-testid="swap-bag-search-team-a"
                            />
                            <span className="text-xs font-black text-slate-400 uppercase tracking-widest">
                                vs
                            </span>
                            <Input
                                placeholder="Equipo B"
                                value={swapTeamB}
                                onChange={(e) => setSwapTeamB(e.target.value)}
                                className="h-10 rounded-xl"
                                data-testid="swap-bag-search-team-b"
                            />
                        </div>
                    )}
                    <div
                        className="mt-2 inline-flex bg-slate-100 rounded-xl p-1"
                        data-testid="swap-bag-tramo-toggle"
                    >
                        {[
                            { v: "all", label: "Todos" },
                            { v: "ida", label: "Solo ida" },
                            { v: "vuelta", label: "Solo vuelta" },
                        ].map((o) => (
                            <button
                                key={o.v}
                                type="button"
                                onClick={() => setTramoFilter(o.v)}
                                className={`px-3 h-7 rounded-lg text-[11px] font-bold uppercase tracking-widest transition-colors ${
                                    tramoFilter === o.v
                                        ? "bg-white text-ipn-900 shadow-sm"
                                        : "text-slate-500 hover:text-ipn-800"
                                }`}
                                data-testid={`swap-bag-tramo-${o.v}`}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                    <div className="max-h-80 overflow-y-auto space-y-2 mt-2">
                        {filteredDrafts.length === 0 && (
                            <div className="text-sm text-slate-500 py-4 text-center">
                                No hay partidos en la bolsa con esos filtros.
                            </div>
                        )}
                        {filteredDrafts.map((d) => (
                            <button
                                key={d.id}
                                onClick={() =>
                                    fillDialog
                                        ? handleFillSlot(d.id)
                                        : handleBagSwap(d.id)
                                }
                                data-testid={`swap-bag-option-${d.id}`}
                                className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-ipn-700 hover:bg-ipn-50 transition-colors"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-bold text-sm truncate flex items-center gap-2">
                                            <span className="truncate">
                                                {d.local} vs {d.visitante}
                                            </span>
                                            {d.vuelta !== undefined && (
                                                <span
                                                    className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest shrink-0 ${
                                                        d.vuelta
                                                            ? "bg-amber-100 text-amber-800"
                                                            : "bg-sky-100 text-sky-800"
                                                    }`}
                                                    data-testid={`swap-bag-tramo-${d.id}`}
                                                >
                                                    {d.vuelta ? "Vuelta" : "Ida"}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                            {d.branch}
                                            {d.category_name
                                                ? ` · ${d.category_name}`
                                                : ""}
                                        </div>
                                    </div>
                                    {fillDialog ? (
                                        <Plus className="w-4 h-4 text-ipn-700 shrink-0" />
                                    ) : (
                                        <ArrowLeftRight className="w-4 h-4 text-ipn-700 shrink-0" />
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function computeSlots(start, end, duration) {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    const slots = [];
    for (let m = startMin; m + duration <= endMin + 1; m += duration) {
        const h = String(Math.floor(m / 60)).padStart(2, "0");
        const mm = String(m % 60).padStart(2, "0");
        slots.push(`${h}:${mm}`);
    }
    if (slots.length === 0) slots.push(start);
    return slots;
}
